import { Factory, Moon, Sun } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { THEME_CYCLE, type ResolvedTheme } from './theme-context';
import { useTheme } from './useTheme';

const META: Record<ResolvedTheme, { icon: LucideIcon; label: string }> = {
  light: { icon: Sun, label: 'Light' },
  dark: { icon: Moon, label: 'Dark' },
  industrial: { icon: Factory, label: 'Industrial' },
};

/**
 * Theme switch for the sidebar footer (86d37f3g8). One button that cycles
 * Light → Dark → Industrial on each click, writing an explicit preference (so
 * the choice sticks even if the OS scheme later changes). The icon shows the
 * theme currently painted; the tooltip names what the next click switches to.
 */
export function ThemeToggle() {
  const { resolvedTheme, setTheme } = useTheme();

  const current = META[resolvedTheme];
  const Icon = current.icon;
  const nextTheme = THEME_CYCLE[(THEME_CYCLE.indexOf(resolvedTheme) + 1) % THEME_CYCLE.length];
  const label = `Theme: ${current.label} — switch to ${META[nextTheme].label}`;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground"
          aria-label={label}
          onClick={() => setTheme(nextTheme)}
        >
          <Icon className="h-5 w-5" />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="top">{label}</TooltipContent>
    </Tooltip>
  );
}
